/**
 * Server functions for organization invite links: the public join page, the
 * invitation on the coach application, the representative's "Invite
 * graduates" page and adding the invited credential from Qualifications.
 * Rules live in `~/lib/server/organization-invites`.
 *
 * Aliases: invite graduates, organization invite, join link
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import type {
  CredentialToAdd,
  OrganizationInvitePage,
} from "~/lib/server/organization-invites";
import {
  credentialRefSchema,
  parseOrganizationInviteCookie,
  type OrganizationInvite,
} from "~/lib/shared/organization-invite";
import { UserFacingError } from "~/lib/shared/user-facing-error";

/**
 * The invitation behind a join link, and whether the visitor is signed in as
 * a coach. Public: it reads published organizations and programs only.
 */
export const getOrganizationJoinFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      slug: z.string().min(1),
      program: z.string().min(1).optional(),
    }),
  )
  .handler(
    async ({
      data,
    }): Promise<{ invite: OrganizationInvite; isCoach: boolean } | null> => {
      const { findOrganizationInvite } =
        await import("~/lib/server/organization-invites");
      const invite = await findOrganizationInvite({
        slug: data.slug,
        programSlug: data.program ?? null,
      });
      if (!invite) return null;
      return { invite, isCoach: await signedInAsCoach() };
    },
  );

async function signedInAsCoach(): Promise<boolean> {
  const { auth } = await import("@clerk/tanstack-react-start/server");
  // A stale session reads as signed out here; the page still works.
  const userId = await auth()
    .then((state) => state.userId)
    .catch(() => null);
  if (!userId) return false;
  const { getCoachByClerkUserId } = await import("~/lib/server/coach");
  return Boolean(await getCoachByClerkUserId(userId));
}

/** The invitation the visitor followed, from the invite cookie. */
export const getMyOrganizationInviteFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async (): Promise<OrganizationInvite | null> => {
    const { getCookie } = await import("@tanstack/react-start/server");
    const { COOKIE_NAMES } = await import("~/lib/cookies");
    const ref = parseOrganizationInviteCookie(
      getCookie(COOKIE_NAMES.organizationInvite),
    );
    if (!ref) return null;
    const { resolveOrganizationInvite } =
      await import("~/lib/server/organization-invites");
    return resolveOrganizationInvite(ref);
  });

export const myOrganizationInviteQueryOptions = () =>
  queryOptions({
    queryKey: ["organization-invite"],
    queryFn: () => getMyOrganizationInviteFn(),
  });

/**
 * The representative's "Invite graduates" page, or null when it is not open
 * to them. It follows Coach claims, which the waiting claims need.
 */
export const getOrganizationInvitePageFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ slug: z.string().min(1) }))
  .handler(
    async ({ data, context }): Promise<OrganizationInvitePage | null> => {
      const { getMyOrganization } =
        await import("~/lib/server/cco-representatives");
      const organization = await getMyOrganization({
        userId: context.user.id,
        slug: data.slug,
      });
      if (!organization) return null;
      const { getOrganizationInvitePage } =
        await import("~/lib/server/organization-invites");
      return getOrganizationInvitePage(organization.ccoId);
    },
  );

export const organizationInvitePageQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["organization-invite-page", slug],
    queryFn: () => getOrganizationInvitePageFn({ data: { slug } }),
  });

/** The published credential behind Qualifications' `?add=`, or null. */
export const getCredentialToAddFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(credentialRefSchema)
  .handler(async ({ data }): Promise<CredentialToAdd | null> => {
    const { findCredentialToAdd } =
      await import("~/lib/server/organization-invites");
    return findCredentialToAdd(data);
  });

/** "Add to my profile": the invited credential, waiting for its organization. */
export const addInvitedCredentialFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(credentialRefSchema)
  .handler(async ({ data, context }): Promise<void> => {
    const { findCredentialToAdd } =
      await import("~/lib/server/organization-invites");
    if (!(await findCredentialToAdd(data))) {
      throw new UserFacingError("This credential can't be added any more.");
    }
    const { addCoachClaim } = await import("~/lib/server/cco-claims");
    await addCoachClaim({
      coachId: context.coach.id,
      ...data,
      certificationDate: null,
    });
  });
